import { createRootRoute, createRoute, createRouter, Outlet } from '@tanstack/react-router';
import { z } from 'zod';
import { ManagerTenantSort, SubscriptionState } from '@odonix/contracts';
import { useSession } from '../lib/session/context.js';
import { AppShell } from '../shell/app-shell.js';
import { PeriodSearch } from '../ui/period.js';
import { HomeScreen } from '../features/home/home-screen.js';
import { TenantsScreen } from '../features/tenants/tenants-screen.js';
import { TenantScreen } from '../features/tenants/tenant-screen.js';
import { LinksScreen } from '../features/links/links-screen.js';

/**
 * Кабинет менеджера: свои показатели, свои клиники, свои ссылки. Отбор списка
 * и период показателей — в адресе, чтобы «назад» из карточки возвращал в тот
 * же список.
 */

const rootRoute = createRootRoute({ component: () => <Outlet /> });

const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'shell',
  component: function Shell() {
    const session = useSession();
    if (session.status !== 'ready') return null;
    return (
      <AppShell manager={session.manager}>
        <Outlet />
      </AppShell>
    );
  },
});

const homeRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/',
  validateSearch: (search: Record<string, unknown>): PeriodSearch => PeriodSearch.parse(search),
  component: HomeScreen,
});

const TenantsSearchSchema = z.object({
  q: z.string().max(200).optional().catch(undefined),
  kind: z.enum(['clinic', 'lab']).optional().catch(undefined),
  state: SubscriptionState.optional().catch(undefined),
  ending: z.coerce.number().int().min(1).max(90).optional().catch(undefined),
  sort: ManagerTenantSort.optional().catch(undefined),
});
export type TenantsSearch = z.infer<typeof TenantsSearchSchema>;

const tenantsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/tenants',
  validateSearch: (search: Record<string, unknown>): TenantsSearch => TenantsSearchSchema.parse(search),
  component: TenantsScreen,
});

const tenantRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/tenants/$tenantId',
  component: function Tenant() {
    const { tenantId } = tenantRoute.useParams();
    return <TenantScreen key={tenantId} tenantId={tenantId} />;
  },
});

const linksRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/links',
  component: LinksScreen,
});

const routeTree = rootRoute.addChildren([
  shellRoute.addChildren([homeRoute, tenantsRoute, tenantRoute, linksRoute]),
]);

export function createAppRouter() {
  return createRouter({ routeTree });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
